import _ from "underscore";
import AppView, { IAppView } from "../../app.view";
import DriveView from "../../DriveView";
import DutyStatusLog, { ALL_EWD_STATUSES, ALL_HOS_STATUSES } from "../../model/DutyStatusLog";
import { dstCalculator, getLocalDaySpan, getNextDayStart } from "../../utils/date";
import rison from "rison";
import { extend } from "../../utils/vanilla";
import HosProcess, { IHosProcessResult } from "../../services/hosProcess";

import {
    hosGraphConstructor as GraphConstructor,
    type IHosViolationSlim,
    type IRestInfo,
    type IGraphOptions,
    type IHosGraphConstructor,
    type IViolation,
    getELDJurisdiction,
    populateViolationReason,
    type IHosRuleset
} from "hos-tools";
import { MobileActions } from "../../store/mobile/actions";
import pageHTML from "./graph.tmpl";

    interface IGraphViewOptions extends IAppView {
        language?: string;
        app: AppView;
    }

    export default class GraphView extends DriveView<Backbone.Model> {

        protected strings = [
            "Certified",
            "Uncertified",
            "Violation",
            "Work Exemption",
            "Edited",
            "Personal Conveyance",
            "Yard move",
            "Qualifying break",
            "minutes",
            "h",
            "{minutes}m",
            "hours",
            "minutes",
            "seconds",
            "Total {0} duration: {1} {2} {3}",
            "Duty Status Graph and HOS Logs",
            "HOS Graph showing:",
            "Total Off Duty: {0}",
            "Total Sleeper Berth: {0}",
            "Total Drive: {0}",
            "Total On Duty: {0}",
            "Total Wait time: {0}",
            "Total Work: {0}",
            "Total Rest: {0}",
            "Grand total: {0}",
            "OFF",
            "SB",
            "D",
            "ON",
            "PC",
            "YM",
            "WT"
        ];
        private readonly template = _.template(pageHTML);
        private readonly hosUtils: HosProcess;
        private readonly onResizeBind;
        private readonly graphConstructor: IHosGraphConstructor;
        private readonly isInspection: boolean;
        private logCollection: DutyStatusLog[] = [];
        private logCollectionInRange: DutyStatusLog[] = [];
        private language?: string;
        private displayedDay: Date;
        private resizeTimeout: number;
        private isZoomed: boolean;
        private violations: IViolation[];

        constructor (options: IGraphViewOptions) {
            super(options);

            this.hosUtils = this.app.hosUtils;
            this.language = options.language;
            this.isInspection = !!this.language;

            this.isZoomed = false;
            this.graphConstructor = GraphConstructor();
            this.onResizeBind = this.onResize.bind(this);
            window.addEventListener("resize", this.onResizeBind);
        }

        /* eslint-disable @typescript-eslint/unbound-method */
        events() {
            return {
                "click .zoomButton": this.toggleGraphView
            } as Backbone.EventsHash;
        }
        /* eslint-enable @typescript-eslint/unbound-method */

        switchLanguage(language: string) {
            if (this.language !== language) {
                this.language = language;
                this.render();
            }
        }

        loadLogs(historicalLogs?: DutyStatusLog[]) {
            const allLogs = historicalLogs ?? this.app.dutyStatusLogService.getHOSLogs(false, undefined, false);
            this.logCollection = this.app.dutyStatusLogService.getSortedHosLogs(allLogs.filter(log => log.isGraphLog()));
        }

        updateDisplayedDate(displayedDate?: Date) {
            if (displayedDate) {
                this.displayedDay = displayedDate;
            }

            let logsWithinDate = this.app.dutyStatusLogService.getLogsByDate(this.logCollection, getLocalDaySpan(this.displayedDay), true, true);
            this.logCollectionInRange = this.app.dutyStatusLogService.clone(logsWithinDate);
            if (this.logCollectionInRange.length > 0 && this.logCollectionInRange[0].getDate() < this.displayedDay) {
                this.logCollectionInRange[0].dateTime = this.displayedDay.toISOString();
            }
        }

        render() {
            void this.translate(this.language).then(() => this.renderPage());
            return this;
        }

        remove() {
            window.removeEventListener("resize", this.onResizeBind);
            super.remove();
            return this;
        }

        // For unit testing only
        testDailyLogs(date: string, logs: DutyStatusLog[]) {
            this.logCollection = logs;
            this.updateDisplayedDate(new Date(date));
            return this.logCollectionInRange;
        }

        private onResize() {
            this.resizeTimeout && clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => this.renderGraph(), 200);
        }

        private getBreaks(ruleResult: IHosProcessResult | undefined, ruleSetConfig: DeepReadonly<IHosRuleset>) {
            let restMin = ruleSetConfig.config.rest ? <number>ruleSetConfig.config.rest.dailyRestMinimum : 0;
            let summaries = ruleResult ? ruleResult.daySummaries : undefined;
            if (!summaries || !restMin) {
                return [];
            }

            let todayRest = summaries.find(daySummary => new Date(daySummary.dateTime).valueOf() === this.displayedDay.valueOf())?.restInfos;
            if (!todayRest) {
                return [];
            }

            return todayRest.reduce((coll: IRestInfo[], restInfo) => {
                let finish = new Date(restInfo.toDate).getTime();
                let start = new Date(restInfo.fromDate).getTime();
                let duration = finish - start;

                if (duration >= restMin && coll.length === 0) {
                    coll.push({
                        fromDate: new Date(start),
                        toDate: new Date(start + restMin)
                    });
                }
                return coll;
            }, []);
        }

        private renderPage() {
            void this.display(this.template({ isZoomed: this.isZoomed })).then(() => this.renderGraph());
        }

        private renderGraph() {
            if (!this.el) {
                // render graph can be called before the page is loaded if refreshing on the page
                return;
            }
            let ruleResult: IHosProcessResult | undefined = this.hosUtils.getRuleResult();
            let ruleSetConfig = this.hosUtils.getDriverRuleSet();
            this.violations = ruleResult ? ruleResult.violations : [];

            let graphRenderArea = this.el.querySelector(".graphRenderContainer");
            if (!graphRenderArea) {
                // The graph has not yet loaded
                return;
            }
            let graphData = {
                data: this.logCollectionInRange,
                fromDate: this.displayedDay,
                toDate: getNextDayStart(this.displayedDay, true),
                violations: this.violations,
                breaks: this.getBreaks(ruleResult, ruleSetConfig)
            };

            let period = getLocalDaySpan(this.displayedDay);
            let dayRuleSetId = this.app.hosUtils.getRuleSet(this.app.dutyStatusLogService.getRuleSetForDate(this.hosUtils, period.toDate).replace("HosRuleSet", "")).id;
            let isEwd = getELDJurisdiction(dayRuleSetId) === "EWD";
            let graphObj: IGraphOptions = {
                fontScale: 6.5,
                fontBold: true,
                skipHours: 2,
                includeViolations: !this.isInspection,
                includeBreaks: !isEwd && !this.isInspection && !!ruleSetConfig.config.rest && !!ruleSetConfig.config.rest.dailyRestMinimum,
                includeEdited: !isEwd,
                includePcYm: !isEwd,
                includeVerified: !isEwd,
                includeWorkExemption: isEwd,
                isEwd,
                translate: this.getString.bind(this),
                getLocalDateTime: dstCalculator.toLocalDateTime,
                customGraphColors: this.getCustomGraphColors(),
                statuses: isEwd ? ["Rest", "Work"] : ["OFF", "SB", "D", "ON"],
                allStatuses: isEwd ? [...ALL_EWD_STATUSES] : [...ALL_HOS_STATUSES]
            };

            if (!this.isInspection && (this.app.store.getState().data.nightMode || window.whitelabel === "lytx")) {
                graphObj = extend(graphObj, { color: { fillColor: "#2B323C", innerLineColor: "#888", outerLineColor: "#fff", approvedColor: "green", modifiedColor: "orange" }});
            }

            graphRenderArea.style.height = `${ (graphRenderArea.clientWidth * 0.29) }px`;

            let logId: string;
            let graphClickHandler = (id: string) => {
                if (!this.isInspection) {
                    let clickedLog: DutyStatusLog | null = this.app.dutyStatusLogService.findWhere(this.logCollectionInRange, "id", id);
                    if (clickedLog) {
                        if (clickedLog.placeholder && clickedLog.parentId) {
                            logId = clickedLog.parentId;
                        } else {
                            logId = id;
                        }

                        let violations: IHosViolationSlim[] = this.violations
                                            .filter(vio => vio.logId === logId)
                                            .map((vio: any) => ({ start: new Date(vio.fromDate).toISOString(), reason: populateViolationReason(vio) }));
                        this.displayedDay && this.app.store.dispatch(MobileActions.setLastViewedLog({ date: this.displayedDay.toISOString() }));
                        this.app.router.navigate("hos/tabs/logs/view," + rison.encode({
                            id: logId,
                            violations: violations
                        }), true);
                    }
                }
            };

            this.graphConstructor.createGraph(
                graphRenderArea,
                {
                    data: graphData.data,
                    fromDate: graphData.fromDate,
                    toDate: graphData.toDate
                },
                graphObj,
                graphClickHandler,
                graphData.violations || [],
                graphData.breaks || []
            );
        }

        private toggleGraphView() {
            this.isZoomed = !this.isZoomed;
            this.renderPage();
        }

        private getCustomGraphColors() {
            if (window.whitelabel === "vodafone") {
                return (status: string) => {
                    switch (status) {
                        case "OFF":
                            return "#e60000";
                        case "SB":
                            return "#eb9700";
                        case "D":
                            return "#007c92";
                        case "ON":
                            return "#a8b400";
                        default:
                            return "#81A3D7";
                    }
                };
            } else if (window.whitelabel === "shell") {
                return (status: string) => {
                    switch (status) {
                        case "OFF":
                            return "#404040";
                        case "SB":
                            return "#DD1D21";
                        case "D":
                            return "#008443";
                        case "ON":
                            return "#FBCE07";
                        default:
                            return "#81A3D7";
                    }
                };
            }
            return undefined;
        }
    }
